<template>
  <section
    class="vue-finder-view"
    :class="{
      'vue-finder-view--compact': compact,
      'vue-finder-view--tree-only': treeOnly
    }"
    aria-label="Gestor de archivos"
    :data-testid="testId"
  >
    <vue-finder
      ref="vueFinder"
      :id="finderId"
      :driver="driver"
      :features="features"
      :config="config"
      :locale="vueFinderLocale"
      :context-menu-items="contextMenuItems"
      @select="handleSelection"
      @path-change="handlePathChange"
      @file-dclick="handleFileDclick"
      @tree-file-click="handleTreeFileClick"
      @ready="handleReady"
      @error="handleError"
    />

    <teleport v-if="toolbarTarget" :to="toolbarTarget">
      <div class="vue-finder-view__creation-actions">
        <el-tooltip :content="$t('ARCHIVOS_crear_carpeta')" effect="dark" placement="top">
          <button
            type="button"
            class="vue-finder-view__create-button"
            :aria-label="$t('ARCHIVOS_crear_carpeta')"
            @click="openCreateForm('carpeta')"
          >
            <el-icon><FolderAdd /></el-icon>
          </button>
        </el-tooltip>
        <el-tooltip :content="$t('ARCHIVOS_crear_archivo')" effect="dark" placement="top">
          <button
            type="button"
            class="vue-finder-view__create-button"
            :aria-label="$t('ARCHIVOS_crear_archivo')"
            @click="openCreateForm('archivo')"
          >
            <el-icon><DocumentAdd /></el-icon>
          </button>
        </el-tooltip>
      </div>
    </teleport>

    <div v-if="treeOnly" class="vue-finder-view__download-action">
      <span
        v-if="selectedDownloadLabel"
        class="vue-finder-view__download-label"
        :title="selectedDownloadLabel"
      >
        {{ selectedDownloadLabel }}
      </span>
      <el-tooltip :content="$t('ARCHIVOS_descargar')" effect="dark" placement="top">
        <el-button
          circle
          class="vue-finder-view__download-button"
          :loading="isDownloading"
          :disabled="!canDownloadSelectedItem || isDownloading"
          :aria-label="$t('ARCHIVOS_descargar')"
          :data-testid="downloadTestId"
          @click="downloadCurrentPath"
        >
          <el-icon v-if="!isDownloading"><Download /></el-icon>
        </el-button>
      </el-tooltip>
    </div>
  </section>
</template>

<script>
import { clientePedido } from '@/apiClient'
import i18n from '@/plugins/i18n'
import { DocumentAdd, FolderAdd } from '@element-plus/icons-vue'
import { contextMenuItems as defaultContextMenuItems, RemoteDriver, useVueFinder } from 'vuefinder'
import { downloadArrayBufferResponse } from '@/utils/archivoDownload'
import { showError } from '../Mensaje'

const STORAGE_KEY = 'SistemaPedidoToken'
const ROOT_PATH = 'colven://'
const DEFAULT_FINDER_ID = 'colvennet-archivos'
const VUEFINDER_LOCALES = ['es', 'en']

function clearPersistedPinnedFolders(finderId) {
  if (typeof window === 'undefined') {
    return
  }

  try {
    const storageKey = `vuefinder_config_${finderId}`
    const storedConfig = window.localStorage.getItem(storageKey)

    if (!storedConfig) {
      return
    }

    const persistedConfig = JSON.parse(storedConfig)

    if (!persistedConfig || typeof persistedConfig !== 'object' || Array.isArray(persistedConfig) ||
      !Array.isArray(persistedConfig.pinnedFolders) || persistedConfig.pinnedFolders.length === 0) {
      return
    }

    window.localStorage.setItem(storageKey, JSON.stringify({
      ...persistedConfig,
      pinnedFolders: []
    }))
  } catch {
    return
  }
}

function getEncodedSession(authentication) {
  if (!authentication || !authentication.Sesion) {
    return undefined
  }

  return btoa(authentication.Sesion)
}

function getPersistedAuthentication() {
  if (typeof window === 'undefined') {
    return undefined
  }

  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY)) || undefined
  } catch {
    return undefined
  }
}

function getDriverBaseUrl() {
  const baseURL = clientePedido.defaults.baseURL || ''
  return `${baseURL.replace(/\/$/, '')}/api/VueFinder`
}

function getVueFinderId(path) {
  const match = /^colven:\/\/(\d+)$/.exec(path || '')
  return match ? Number(match[1]) : undefined
}

function getLogicalPath(path) {
  if (typeof path !== 'string') {
    return undefined
  }

  return path === ROOT_PATH || /^colven:\/\/\d+$/.test(path) ? path : undefined
}

class ColvenRemoteDriver extends RemoteDriver {
  constructor(config) {
    super(config)
    this.onList = null
  }

  async list(params) {
    const response = await super.list(params)

    if (this.onList) {
      this.onList(response)
    }

    return response
  }
}

function createDriver(token) {
  return new ColvenRemoteDriver({
    baseURL: getDriverBaseUrl(),
    token,
    url: {
      list: '/List',
      search: '/Search',
      delete: '/Delete',
      move: '/Move',
      rename: '/Rename',
      download: undefined,
      preview: undefined
    }
  })
}

export default {
  name: 'VueFinderView',
  components: { DocumentAdd, FolderAdd },
  props: {
    initialPath: {
      type: String,
      default: undefined
    },
    /**
     * Compact layout for embedding in Home aside.
     */
    compact: {
      type: Boolean,
      default: false
    },
    treeOnly: {
      type: Boolean,
      default: false
    },
    showMenuBar: {
      type: Boolean,
      default: false
    },
    showTreeView: {
      type: Boolean,
      default: true
    },
    showFilesInTree: {
      type: Boolean,
      default: false
    },
    theme: {
      type: String,
      default: 'mythril'
    },
    finderId: {
      type: String,
      default: DEFAULT_FINDER_ID
    },
    testId: {
      type: String,
      default: undefined
    },
    downloadTestId: {
      type: String,
      default: undefined
    }
  },
  data() {
    const authentication = this.$store.state.sesion.resultadoAutenticacion || getPersistedAuthentication()

    return {
      driver: createDriver(getEncodedSession(authentication)),
      activePath: undefined,
      fullscreenObserver: null,
      toolbarObserver: null,
      isDownloading: false,
      folderNames: {},
      selectedTreeFile: null,
      toolbarTarget: null,
      treeHydrationPath: null
    }
  },
  computed: {
    currentPath() {
      return this.activePath || this.$store.getters['archivos/vueFinderPath'] || ROOT_PATH
    },
    vueFinderLocale() {
      return VUEFINDER_LOCALES.includes(i18n.locale) ? i18n.locale : 'es'
    },
    capabilities() {
      return this.$store.getters['archivos/vueFinderCapabilities'] || {
        delete: false,
        move: false
      }
    },
    features() {
      return {
        search: true,
        pinned: false,
        download: false,
        preview: false,
        upload: false,
        newfolder: false,
        newfile: false,
        delete: this.capabilities.delete,
        rename: false,
        move: this.capabilities.move,
        copy: false,
        archive: false,
        unarchive: false
      }
    },
    contextMenuItems() {
      return [
        ...defaultContextMenuItems,
        {
          id: 'colven-create-folder',
          title: () => this.$t('ARCHIVOS_crear_carpeta'),
          action: (app) => {
            if (this.treeOnly || !app) {
              return
            }

            this.openCreateForm('carpeta')
          },
          show: (app, context) => Boolean(
            !this.treeOnly &&
            app &&
            this.isBackgroundContext(context)
          ),
          order: 12
        },
        {
          id: 'colven-create-file',
          title: () => this.$t('ARCHIVOS_crear_archivo'),
          action: (app) => {
            if (this.treeOnly || !app) {
              return
            }

            this.openCreateForm('archivo')
          },
          show: (app, context) => Boolean(
            !this.treeOnly &&
            app &&
            this.isBackgroundContext(context)
          ),
          order: 13
        },
        {
          id: 'colven-edit',
          title: () => this.$t('ARCHIVOS_editar_archivo'),
          action: (app, selectedItems) => {
            if (this.treeOnly || !app || !Array.isArray(selectedItems) || selectedItems.length !== 1) {
              return
            }

            this.editVueFinderItem(selectedItems[0])
          },
          show: (app, context) => Boolean(
            !this.treeOnly &&
            app &&
            context &&
            context.target &&
            Array.isArray(context.items) &&
            context.items.length === 1 &&
            this.isEditableItem(context.target)
          ),
          order: 14
        },
        {
          id: 'colven-download',
          title: () => this.$t('ARCHIVOS_descargar'),
          action: (app, selectedItems) => {
            if (!app) {
              return
            }

            this.downloadItems(selectedItems)
          },
          show: (app, context) => Boolean(
            app && context && context.target && this.isDownloadableItem(context.target)
          ),
          order: 15
        }
      ]
    },
    config() {
      return {
        initialPath: this.currentPath,
        // A persisted path can point to a folder that is no longer visible to the user.
        // Always resolve the initial listing from the current session and root path.
        persist: false,
        theme: this.theme,
        view: 'list',
        showTreeView: this.showTreeView || this.treeOnly,
        showFilesInTree: this.showFilesInTree,
        expandTreeByDefault: false,
        expandedTreePaths: [ROOT_PATH],
        showMenuBar: this.showMenuBar && !this.treeOnly,
        showToolbar: !this.treeOnly
      }
    },
    sessionToken() {
      return getEncodedSession(this.$store.state.sesion.resultadoAutenticacion)
    },
    canDownloadCurrentPath() {
      return this.treeOnly && Boolean(getVueFinderId(this.currentPath))
    },
    selectedDownloadItem() {
      if (this.selectedTreeFile && this.isDownloadableItem(this.selectedTreeFile)) {
        return this.selectedTreeFile
      }

      if (!this.canDownloadCurrentPath) {
        return null
      }

      return {
        path: this.currentPath,
        type: 'dir',
        basename: this.selectedFolderLabel || this.$t('ARCHIVOS_archivos')
      }
    },
    canDownloadSelectedItem() {
      return Boolean(this.selectedDownloadItem)
    },
    selectedDownloadLabel() {
      return this.selectedTreeFile && this.selectedTreeFile.basename
        ? this.selectedTreeFile.basename
        : this.selectedFolderLabel
    },
    selectedFolderLabel() {
      return this.canDownloadCurrentPath ? this.folderNames[this.currentPath] || '' : ''
    }
  },
  watch: {
    sessionToken(token) {
      this.driver.setToken(token)
      this.$store.commit('archivos/setVueFinderCapabilities', undefined)
      if (token) {
        this.loadCapabilities()
      }
    }
  },
  methods: {
    openCreateForm(tipo) {
      const tipoInicial = tipo === 'carpeta' ? 'carpeta' : 'archivo'

      this.$router.push({
        path: '/archivos/nuevo',
        query: {
          tipo: tipoInicial,
          path: this.currentPath,
          returnPath: this.currentPath
        }
      })
    },
    setupCreationToolbar() {
      if (this.treeOnly) {
        return
      }

      this.$nextTick(() => {
        const root = this.getVueFinderRoot()
        if (!root) {
          return
        }

        const targetName = `vuefinder-create-controls-${String(this.finderId).replace(/[^a-zA-Z0-9_-]/g, '-')}`
        const attachCreationToolbar = () => {
          const toolbarControls = root.querySelector('.vuefinder__toolbar__controls')

          if (!toolbarControls) {
            return false
          }

          toolbarControls.setAttribute('data-colven-create-controls', targetName)
          this.toolbarTarget = `[data-colven-create-controls="${targetName}"]`
          return true
        }

        if (attachCreationToolbar() || typeof MutationObserver === 'undefined') {
          return
        }

        this.toolbarObserver = new MutationObserver(() => {
          if (attachCreationToolbar()) {
            this.toolbarObserver.disconnect()
            this.toolbarObserver = null
          }
        })
        this.toolbarObserver.observe(root, { childList: true, subtree: true })
      })
    },
    loadCapabilities() {
      return this.$store.dispatch('archivos/getVueFinderCapabilities')
    },
    handlePathChange(path) {
      const logicalPath = getLogicalPath(path) || ROOT_PATH
      this.activePath = logicalPath
      this.selectedTreeFile = null
      this.$store.commit('archivos/setVueFinderPath', logicalPath)
      this.$emit('path-change', logicalPath)
    },
    handleReady() {
      this.hydrateTree(this.currentPath)
    },
    async hydrateTree(path) {
      const targetPath = getLogicalPath(path)

      if (!targetPath || targetPath === ROOT_PATH || this.treeHydrationPath === targetPath) {
        return
      }

      this.treeHydrationPath = targetPath

      try {
        const vueFinder = useVueFinder(this.finderId)
        await vueFinder.open(ROOT_PATH)
        await vueFinder.open(targetPath)
      } catch {
        this.treeHydrationPath = null
      }
    },
    handleSelection(items) {
      const selectedFolder = (Array.isArray(items) ? items : [items])
        .find(item => item && item.type === 'dir' && item.path && item.basename)

      if (selectedFolder) {
        this.folderNames = {
          ...this.folderNames,
          [selectedFolder.path]: selectedFolder.basename
        }
      }

      this.$emit('select', items)
    },
    handleListResponse(response) {
      const folderNames = (response && Array.isArray(response.files) ? response.files : [])
        .filter(item => item && item.type === 'dir' && item.path && item.basename)
        .reduce((names, item) => ({
          ...names,
          [item.path]: item.basename
        }), {})

      if (Object.keys(folderNames).length) {
        this.folderNames = {
          ...this.folderNames,
          ...folderNames
        }
      }
    },
    handleFileDclick(event) {
      if (!event || !event.item) {
        return
      }

      event.preventDefault()
      this.downloadItems([event.item])
    },
    handleTreeFileClick(item) {
      if (!item || item.type !== 'file') {
        return
      }

      this.selectedTreeFile = item
      this.$emit('tree-file-click', item)
    },
    isEditableItem(item) {
      return Boolean(item && getVueFinderId(item.path) && (item.type === 'file' || item.type === 'dir'))
    },
    isBackgroundContext(context) {
      return Boolean(
        context &&
        !context.target &&
        Array.isArray(context.items)
      )
    },
    editVueFinderItem(item) {
      const idArchivo = getVueFinderId(item && item.path)

      if (!idArchivo) {
        return
      }

      this.$router.push({
        path: `/archivos/edicion/${idArchivo}`,
        query: {
          returnPath: getLogicalPath(item && item.dir) || this.currentPath
        }
      })
    },
    getAuthentication() {
      return this.$store.state.sesion.resultadoAutenticacion || getPersistedAuthentication() || {}
    },
    isLinkItem(item) {
      return item && (
        item.colven_type === 'link' ||
        (item.type === 'file' && item.mime_type === null && item.file_size === null)
      )
    },
    isDownloadableItem(item) {
      return Boolean(item && getVueFinderId(item.path) && (item.type === 'file' || item.type === 'dir'))
    },
    downloadCurrentPath() {
      if (!this.selectedDownloadItem) {
        return
      }

      this.downloadItems([this.selectedDownloadItem])
    },
    async downloadItems(items) {
      if (this.isDownloading) {
        return
      }

      const downloadableItems = (Array.isArray(items) ? items : [items])
        .filter(item => this.isDownloadableItem(item))

      if (!downloadableItems.length) {
        return
      }

      this.isDownloading = true

      try {
        for (const item of downloadableItems) {
          await this.downloadItem(item)
        }
      } catch {
        showError(this.$t('ARCHIVOS_error_descarga'))
      } finally {
        this.isDownloading = false
      }
    },
    async downloadItem(item) {
      const idNodo = getVueFinderId(item.path)
      const authentication = this.getAuthentication()
      const idPerfiles = Array.isArray(authentication.Perfiles)
        ? authentication.Perfiles.map(profile => profile.Id).filter(Boolean)
        : []

      if (!idNodo || !authentication.IdUsuario) {
        throw new Error('La descarga requiere un elemento y una sesión válidos')
      }

      if (this.isLinkItem(item)) {
        const link = await this.$store.dispatch('archivos/getLink', { idArchivo: idNodo })

        if (!link) {
          throw new Error('El enlace del archivo está vacío')
        }

        window.open(link, '_blank', 'noopener,noreferrer')
        return
      }

      const response = await this.$store.dispatch('archivos/downloadFile', {
        idUsuario: authentication.IdUsuario,
        idPerfiles,
        idNodo
      })

      downloadArrayBufferResponse(response, item.basename)
    },
    handleError(error) {
      this.$emit('error', error)
    },
    getVueFinderRoot() {
      const vueFinder = this.$refs.vueFinder
      return vueFinder && (vueFinder.$el || vueFinder)
    },
    syncFullscreenState() {
      const root = this.getVueFinderRoot()
      const isFullscreen = !!(root && (
        root.matches('.vuefinder__main__fixed') ||
        root.querySelector('.vuefinder__main__fixed')
      ))

      this.$store.commit('archivos/setVueFinderFullscreen', isFullscreen)
    },
    observeFullscreenState() {
      this.$nextTick(() => {
        const root = this.getVueFinderRoot()

        if (!root) {
          return
        }

        this.syncFullscreenState()

        if (typeof MutationObserver === 'undefined') {
          return
        }

        this.fullscreenObserver = new MutationObserver(() => {
          this.syncFullscreenState()
        })
        this.fullscreenObserver.observe(root, {
          subtree: true,
          attributes: true,
          attributeFilter: ['class']
        })
      })
    }
  },
  created() {
    this.activePath = getLogicalPath(this.initialPath) ||
      getLogicalPath(this.$route.query.path) ||
      getLogicalPath(this.$store.getters['archivos/vueFinderPath']) ||
      ROOT_PATH
    clearPersistedPinnedFolders(this.finderId)
    this.driver.onList = this.handleListResponse.bind(this)
  },
  mounted() {
    this.$store.commit('archivos/setVueFinderCapabilities', undefined)

    if (!this.$store.state.sesion.resultadoAutenticacion) {
      const token = JSON.parse(window.localStorage.getItem(STORAGE_KEY))
      this.driver.setToken(getEncodedSession(token))
    }

    this.loadCapabilities()
    this.setupCreationToolbar()
    this.observeFullscreenState()
  },
  beforeUnmount() {
    if (this.fullscreenObserver) {
      this.fullscreenObserver.disconnect()
    }

    if (this.toolbarObserver) {
      this.toolbarObserver.disconnect()
    }

    this.$store.commit('archivos/setVueFinderFullscreen', false)
  }
}
</script>

<style scoped>
.vue-finder-view {
  min-height: 620px;
  width: 100%;
}

.vue-finder-view--compact {
  min-height: 320px;
  height: 360px;
  max-height: 360px;
  overflow: hidden;
}

.vue-finder-view--compact :deep(.vuefinder) {
  height: 100%;
  min-height: 0;
}

.vue-finder-view--compact :deep(.vuefinder__main),
.vue-finder-view--compact :deep(.vuefinder__body),
.vue-finder-view--compact :deep(.vuefinder__content) {
  min-height: 0;
}

.vue-finder-view--tree-only {
  min-height: 240px;
  height: 280px;
  max-height: 280px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.vue-finder-view__download-action {
  display: flex;
  flex: 0 0 40px;
  align-items: center;
  gap: 8px;
  justify-content: flex-start;
  min-width: 0;
  padding: 4px 6px;
}

.vue-finder-view__creation-actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  order: -1;
  gap: 12px;
  margin-inline: 6px;
}

.vue-finder-view__create-button {
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: var(--vf-text-secondary, var(--ds-color-neutral-600));
  cursor: pointer;
}

.vue-finder-view__create-button:hover {
  background: var(--vf-bg-hover, var(--ds-color-neutral-100));
  color: var(--vf-accent-primary, var(--main-color));
}

.vue-finder-view__create-button:focus-visible {
  outline: 2px solid var(--vf-accent-primary, var(--main-color));
  outline-offset: 1px;
}

.vue-finder-view__create-button :deep(.el-icon) {
  font-size: 32px;
}

.vue-finder-view__download-label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  color: var(--ds-color-neutral-600);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vue-finder-view__download-button {
  flex: 0 0 auto;
  margin-left: auto;
}

.vue-finder-view--tree-only :deep(.vuefinder) {
  flex: 1 1 auto;
  height: auto;
  min-height: 0;
}

.vue-finder-view--tree-only :deep(.vuefinder__breadcrumb__container),
.vue-finder-view--tree-only :deep(.vuefinder__explorer__container),
.vue-finder-view--tree-only :deep(.vuefinder__status-bar__wrapper) {
  display: none;
}

.vue-finder-view--tree-only :deep(.vuefinder__main__content) {
  display: block;
  min-height: 0;
  overflow: hidden;
}

.vue-finder-view--tree-only :deep(.vuefinder__treeview__container) {
  height: 100%;
  width: 100% !important;
  max-width: 100%;
  min-width: 0 !important;
}

.vue-finder-view--tree-only :deep(.vuefinder__treeview__scroll) {
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  border-right: none;
}

.vue-finder-view--tree-only :deep(.vuefinder),
.vue-finder-view--tree-only :deep(.vuefinder__treeview__container),
.vue-finder-view--tree-only :deep(.vuefinder__treeview__scroll),
.vue-finder-view--tree-only :deep(.vuefinder__treestorageitem__header),
.vue-finder-view--tree-only :deep(.vuefinder__treestorageitem__subfolder) {
  background-color: var(--ds-color-neutral-0);
}

:global(.vuefinder__modal-layout .vf-btn-danger:not([disabled])) {
  background-color: var(--ds-color-error-400);
  border-color: var(--ds-color-error-400);
  color: var(--ds-color-neutral-0);
  opacity: 1;
}

:global(.vuefinder__modal-layout .vf-btn-danger:not([disabled]):hover) {
  background-color: var(--ds-color-error-500);
  border-color: var(--ds-color-error-500);
}

:global(.vuefinder__modal-layout .vf-btn-danger:not([disabled]):focus-visible) {
  box-shadow: 0 0 0 3px var(--ds-color-error-200);
}

:global(.vuefinder__modal-layout .vf-btn-danger[disabled]) {
  background-color: var(--ds-color-neutral-200);
  border-color: var(--ds-color-neutral-300);
  color: var(--ds-color-neutral-500);
  cursor: not-allowed;
  opacity: 1;
}
</style>